/**
 * EditorCanvas — interactive Konva editor surface.
 *
 * Chunk 3 patch (Apr 24 2026): rewrite of the drag/selection model.
 *
 * Before: each element was rendered at absolute coords + a hit-rect
 * overlay. Dragging the hit-rect while the element itself stayed
 * put, then state update snapped it. Click + drag both felt clunky
 * because the drag ran on a transparent proxy.
 *
 * After: each element is wrapped in a Group positioned at element.x,
 * element.y mm (scaled to px). Inner primitive is rendered at 0,0
 * relative coords. The Group itself is draggable and Transformer-
 * target, so there's nothing fighting the interaction.
 *
 * Inner-at-zero rendering: we pass a temporary element-with-zero-
 * origin to dispatchElementKonva so the primitive emits nodes at
 * (0,0). The Group does the translation.
 *
 * Perf wins:
 *   - Layout build memoised
 *   - Content resolution deps narrowed
 *   - Transformer attach only on selection change, not every mutation
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Rect, Transformer, Text as KonvaText, Group } from 'react-konva';
import {
  dispatchElementKonva,
  sortByZ,
  renderWaveBandKonva,
  renderMountainsKonva,
  renderBannerImageKonva,
} from '../../lib/cards/konva-renderers.js';
import {
  buildMinimalLayout,
  buildPremiumLayout,
} from '../../lib/cards/build-runtime-layout.js';
import { clampElement, canDelete, snapToGrid, MIN_DIMENSIONS } from '../../lib/cards/layout-constraints.js';
import { CR80, generateQR, verifyUrl, deriveInitials } from '../../lib/cards/shared.js';

const SCALE = 4;
const MOCK_STAFF = {
  first_name: 'Dani',
  last_name: 'Morgan',
  role: 'Senior Cleaner',
  photo_url: null,
};

export function EditorCanvas({ editor, tenant = {}, staff = MOCK_STAFF }) {
  const { layout, selectedId, select, moveElement, resizeElement, deleteElement,
          undo, redo } = editor;

  const transformerRef = useRef(null);
  const groupRefs = useRef(new Map());  // id → Konva Group node
  const containerRef = useRef(null);
  const [contentMap, setContentMap] = useState({});

  // Derived procedural bits — memoised on tenant/staff identity
  const template = layout?.basedOnTemplate || tenant.card_template || 'minimal';

  const { derived } = useMemo(() => {
    return template === 'premium'
      ? buildPremiumLayout(tenant, staff)
      : buildMinimalLayout(tenant, staff);
  }, [
    template,
    tenant.brand_color, tenant.card_company_name, tenant.name,
    tenant.card_photo_size, tenant.card_name_font_size,
    tenant.card_footer_text_color, tenant.card_banner_opacity,
    tenant.card_bottom_image_url, tenant.display_name_mode,
    tenant.public_phone, tenant.public_website,
    staff.first_name, staff.last_name, staff.role,
  ]);

  /* ─── Content resolution (narrow deps) ─── */

  const logoUrl = tenant.logo_url;
  const tenantSlug = tenant.slug;
  const bannerUrl = tenant.card_bottom_image_url;
  const photoUrl = staff.photo_url;
  const staffSlug = staff.slug;
  const staffRole = staff.role;
  const displayName = derived.displayName;
  const footerText = derived.footerText;
  const hasFooter = derived.hasFooter;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      if (logoUrl) {
        try {
          const img = await loadImage(logoUrl);
          next.el_logo = { image: img, width: img.naturalWidth, height: img.naturalHeight };
        } catch (e) {}
      }
      if (photoUrl) {
        try {
          const img = await loadImage(photoUrl);
          next.el_staff_photo = { image: img };
        } catch (e) {}
      }
      try {
        const qrUrl = verifyUrl(tenantSlug || 'demo', staffSlug || 'sample');
        const qrDataUrl = await generateQR(qrUrl, 512);
        const qrImg = await loadImage(qrDataUrl);
        next.el_qr = { image: qrImg };
      } catch (e) {}
      if (template === 'premium' && bannerUrl) {
        try {
          const img = await loadImage(bannerUrl);
          next.__banner = { image: img };
        } catch (e) {}
      }
      next.el_name = { text: displayName };
      if (staffRole) next.el_role = { text: staffRole };
      if (hasFooter && template === 'minimal') next.el_footer_text = { text: footerText };
      if (!cancelled) setContentMap(next);
    })();
    return () => { cancelled = true; };
  }, [logoUrl, tenantSlug, bannerUrl, photoUrl, staffSlug, staffRole,
      template, displayName, footerText, hasFooter]);

  /* ─── Transformer attach (selection-only dep) ─── */

  useEffect(() => {
    if (!transformerRef.current) return;
    if (selectedId) {
      const node = groupRefs.current.get(selectedId);
      if (node) {
        transformerRef.current.nodes([node]);
      } else {
        transformerRef.current.nodes([]);
      }
    } else {
      transformerRef.current.nodes([]);
    }
    transformerRef.current.getLayer()?.batchDraw();
  }, [selectedId]);

  /* ─── Keyboard ─── */

  useEffect(() => {
    const onKeyDown = (e) => {
      // Don't hijack keys when typing in form fields
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        // Still allow undo/redo from anywhere
        if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
          e.preventDefault();
          if (e.shiftKey) redo(); else undo();
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }

      if (!selectedId || !layout) return;
      const selected = layout.elements.find((x) => x.id === selectedId);
      if (!selected) return;
      const step = e.shiftKey ? 2 : 0.5;

      if (e.key === 'Escape') {
        e.preventDefault();
        select(null);
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && canDelete(selected)) {
        e.preventDefault();
        deleteElement(selectedId);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        nudge(selected, -step, 0);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        nudge(selected, step, 0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        nudge(selected, 0, -step);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        nudge(selected, 0, step);
      }
    };

    const nudge = (sel, dx, dy) => {
      const next = clampElement(
        { x: sel.x + dx, y: sel.y + dy, w: sel.w, h: sel.h },
        sel.type,
      );
      moveElement(sel.id, { x: next.x, y: next.y });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedId, layout, redo, undo, select, deleteElement, moveElement]);

  if (!layout) return null;

  const cardW = CR80.widthMm;
  const cardH = CR80.heightMm;
  const stageW = cardW * SCALE;
  const stageH = cardH * SCALE;

  const ctx = { brandColor: derived.brandColor, tenant, staff };
  const initials = deriveInitials(staff.first_name, staff.last_name);

  const elements = sortByZ(layout.elements);

  return (
    <div
      ref={containerRef}
      style={{
        width: stageW,
        maxWidth: '100%',
        margin: '0 auto',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        background: '#fff',
      }}
    >
      <Stage
        width={stageW}
        height={stageH}
        onMouseDown={(e) => {
          if (e.target === e.target.getStage()) select(null);
        }}
        onTouchStart={(e) => {
          if (e.target === e.target.getStage()) select(null);
        }}
      >
        {/* Background + procedural layer — non-interactive */}
        <Layer listening={false}>
          <Rect x={0} y={0} width={stageW} height={stageH} fill="#FFFFFF" />
          {template === 'minimal' && renderWaveBandKonva(
            derived.brandColor, derived.waveBand?.topY || 49, cardW, cardH, SCALE,
          )}
          {template === 'premium' && (
            contentMap.__banner?.image
              ? renderBannerImageKonva(
                  contentMap.__banner.image,
                  derived.bannerOpacity,
                  derived.banner,
                  derived.brandColor,
                  cardW, cardH, SCALE,
                )
              : renderMountainsKonva(
                  derived.brandColor, derived.banner?.topY || 41,
                  cardW, cardH, SCALE,
                )
          )}
        </Layer>

        {/* Interactive layer */}
        <Layer>
          {elements.map((el) => (
            <DraggableElement
              key={el.id}
              element={el}
              content={contentMap[el.id]}
              ctx={ctx}
              initials={initials}
              derived={derived}
              isSelected={selectedId === el.id}
              groupRefs={groupRefs}
              onSelect={() => select(el.id)}
              onDragEnd={(xMm, yMm) => {
                const clamped = clampElement(
                  { x: xMm, y: yMm, w: el.w, h: el.h },
                  el.type,
                );
                moveElement(el.id, { x: clamped.x, y: clamped.y });
              }}
              onTransformEnd={(next) => {
                const clamped = clampElement({ ...el, ...next }, el.type);
                resizeElement(el.id, clamped);
              }}
            />
          ))}

          <Transformer
            ref={transformerRef}
            rotateEnabled={false}
            flipEnabled={false}
            borderStroke="#00AB39"
            anchorStroke="#00AB39"
            anchorFill="#FFFFFF"
            anchorSize={8}
            boundBoxFunc={(oldBox, newBox) => {
              if (newBox.width < 3 * SCALE || newBox.height < 3 * SCALE) return oldBox;
              return newBox;
            }}
          />
        </Layer>
      </Stage>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  DraggableElement — Group wrapping inner nodes at zero origin.   */
/* ─────────────────────────────────────────────────────────────── */

function DraggableElement({
  element, content, ctx, initials, derived,
  isSelected, groupRefs, onSelect, onDragEnd, onTransformEnd,
}) {
  const groupRef = useRef(null);

  useEffect(() => {
    if (groupRef.current) {
      groupRefs.current.set(element.id, groupRef.current);
    }
    return () => {
      groupRefs.current.delete(element.id);
    };
  }, [element.id, groupRefs]);

  // Render inner at (0,0) by passing a zero-origin element clone
  const zeroEl = { ...element, x: 0, y: 0 };
  let inner = dispatchElementKonva(zeroEl, content, ctx, SCALE, `in-${element.id}-`);

  // Fallbacks for missing images (zero-origin too)
  if (!inner) {
    if (element.type === 'dynamic_photo') {
      inner = (
        <>
          <Rect
            x={0} y={0}
            width={element.w * SCALE}
            height={element.h * SCALE}
            fill="#F4E9D6"
          />
          <KonvaText
            x={0} y={0}
            width={element.w * SCALE}
            height={element.h * SCALE}
            text={initials || '?'}
            fontSize={(element.w > 25 ? 22 : 18) * 0.3528 * SCALE}
            fontFamily="Helvetica, Arial, sans-serif"
            fontStyle="bold"
            fill="#6B7A70"
            align="center"
            verticalAlign="middle"
          />
        </>
      );
    } else if (element.type === 'dynamic_logo' && derived.logoFallback) {
      const f = derived.logoFallback;
      inner = (
        <KonvaText
          x={0}
          y={0}
          width={element.w * SCALE}
          height={element.h * SCALE}
          text={f.text}
          fontSize={f.fontSize * 0.3528 * SCALE}
          fontFamily="Helvetica, Arial, sans-serif"
          fontStyle="bold"
          fill={f.color}
          verticalAlign="middle"
        />
      );
    }
  }

  // Invisible sizing rect so the Group has solid hit area covering
  // the full element bounds (Konva Group hit-testing is bounding-box-
  // of-children; this guarantees clicks anywhere on the element
  // register).
  const hitRect = (
    <Rect
      x={0} y={0}
      width={element.w * SCALE}
      height={element.h * SCALE}
      fill="transparent"
    />
  );

  return (
    <Group
      ref={groupRef}
      x={element.x * SCALE}
      y={element.y * SCALE}
      draggable
      onMouseDown={(e) => {
        e.cancelBubble = true;
        onSelect();
      }}
      onTouchStart={(e) => {
        e.cancelBubble = true;
        onSelect();
      }}
      onDragEnd={(e) => {
        const node = e.target;
        const newXmm = snapToGrid(node.x() / SCALE);
        const newYmm = snapToGrid(node.y() / SCALE);
        onDragEnd(newXmm, newYmm);
      }}
      onTransformEnd={(e) => {
        const node = e.target;
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        node.scaleX(1);
        node.scaleY(1);

        const newW = snapToGrid((element.w * scaleX));
        const newH = snapToGrid((element.h * scaleY));
        const newX = snapToGrid(node.x() / SCALE);
        const newY = snapToGrid(node.y() / SCALE);

        onTransformEnd({ x: newX, y: newY, w: newW, h: newH });
      }}
    >
      {hitRect}
      {inner}
      {isSelected && (
        <Rect
          x={-1}
          y={-1}
          width={element.w * SCALE + 2}
          height={element.h * SCALE + 2}
          stroke="#00AB39"
          strokeWidth={1}
          dash={[3, 3]}
          listening={false}
        />
      )}
    </Group>
  );
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    img.src = src;
  });
}
